import { useEffect, useLayoutEffect } from 'react';
import { Outlet, useLocation, useNavigationType } from 'react-router';

import { useThemeRouteOverride } from '@/theme/useTheme';

import { MarketingNavBar } from './components/MarketingNavBar';
import { SiteFooter } from './components/SiteFooter';

import './marketing-tokens.css';
import styles from './MarketingLayout.module.css';

// Italic 600 is the emphasised phrase in the display headings.
const MARKETING_FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,600&display=swap';

/**
 * A link to another marketing page opens it at the top, or at the element its
 * `#` fragment names. Without this the new page inherits the old one's scroll
 * position, and a link such as `/ai#how-kai-works` changes the URL without
 * moving. Back and forward (POP) are left to the browser; so are plain in-page
 * `<a href="#…">` clicks, which the router also sees as POP.
 */
function useMarketingScrollOnNavigate() {
  const location = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    if (navigationType === 'POP') {
      return;
    }

    const id = location.hash ? decodeURIComponent(location.hash.slice(1)) : '';
    const target = id ? document.getElementById(id) : null;

    if (target) {
      target.scrollIntoView();
    } else {
      window.scrollTo(0, 0);
    }
    // location.key changes on every navigation, including to the same path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
}

export function MarketingLayout() {
  useThemeRouteOverride('light');
  useMarketingScrollOnNavigate();

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    let linkElement = document.head.querySelector<HTMLLinkElement>(
      'link[data-marketing-playfair="true"]',
    );
    let ownsElement = false;

    if (!linkElement) {
      linkElement = document.createElement('link');
      linkElement.rel = 'stylesheet';
      linkElement.href = MARKETING_FONT_HREF;
      linkElement.dataset.marketingPlayfair = 'true';
      document.head.appendChild(linkElement);
      ownsElement = true;
    }

    return () => {
      if (ownsElement && linkElement?.parentNode) {
        linkElement.parentNode.removeChild(linkElement);
      }
    };
  }, []);

  return (
    <div className={styles.shell} data-marketing-root="true">
      <MarketingNavBar />
      <main className={styles.main}>
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
}
